import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { soccerPath, soccerUrl } from '../utils/paths';
import './MatchScheduling.css';

const MatchScheduling = () => {
  const { id: clubId } = useParams();
  const navigate = useNavigate();
  const { user, isClubAdmin } = useAuth();
  const [matches, setMatches] = useState([]);
  const [club, setClub] = useState(null);
  const [userMember, setUserMember] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    matchDate: '',
    location: '',
    opponentName: '',
  });
  const [matchSummaries, setMatchSummaries] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadClubData = useCallback(async () => {
    try {
      const clubData = await api.getClubById(clubId);
      setClub(clubData);
      
      // Check if user is a match coordinator
      if (user) {
        const members = await api.getClubMembers(clubId, null, user.id);
        const member = members.find(m => 
          m.player?.userId === user.id || 
          (m.player && m.player.id && m.player.email === user.email)
        );
        if (member) {
          setUserMember(member);
        }
      }
    } catch (err) {
      console.error('Failed to load club data:', err);
    }
  }, [clubId, user]);

  const loadMatches = useCallback(async () => {
    try {
      const data = await api.getMatchesByClub(clubId);
      setMatches(data);
    } catch (err) {
      setError(err.message || 'Failed to load matches');
    }
  }, [clubId]);

  useEffect(() => {
    loadMatches();
    loadClubData();
  }, [clubId, loadMatches, loadClubData]);

  const canShareLink = () => {
    if (!user) return false;
    if (isClubAdmin() && club?.adminUserId === user.id) return true;
    if (userMember?.roles?.includes('MATCH_COORDINATOR')) return true;
    return false;
  };

  const shareVoteLink = (matchId) => {
    const voteUrl = soccerUrl(`/matches/${matchId}`);
    const match = matches.find(m => m.id === matchId);
    
    if (!match) return;

    // Copy to clipboard
    navigator.clipboard.writeText(voteUrl).then(() => {
      setSuccess('Vote link copied to clipboard!');
      setTimeout(() => setSuccess(''), 3000);
    }).catch(() => {
      setError('Failed to copy link');
      setTimeout(() => setError(''), 3000);
    });

    // Helper function to properly encode message for WhatsApp with emoji support
    const encodeWhatsAppMessage = (message) => {
      return encodeURIComponent(message);
    };

    // Get WhatsApp URL - use api.whatsapp.com for better emoji support
    const getWhatsAppUrl = (message) => {
      const encodedMessage = encodeWhatsAppMessage(message);
      return `https://api.whatsapp.com/send?text=${encodedMessage}`;
    };

    // Also prepare WhatsApp message
    const message = `⚽ *${match.title}*\n\n` +
      `📅 Date: ${new Date(match.matchDate).toLocaleString()}\n` +
      (match.location ? `📍 Location: ${match.location}\n` : '') +
      `\n🗳️ Vote your availability:\n${voteUrl}`;
    
    // Use api.whatsapp.com for better emoji support
    const whatsappUrl = getWhatsAppUrl(message);
    
    // Open WhatsApp in new window after a short delay
    setTimeout(() => {
      window.open(whatsappUrl, '_blank');
    }, 500);
  };

  const loadSummaries = useCallback(async () => {
    const summaries = {};
    for (const match of matches) {
      const matchDate = new Date(match.matchDate);
      const isPast = matchDate < new Date();
      const canHaveSummary = match.status === 'COMPLETED' || (match.status === 'SCHEDULED' && isPast);
      
      if (canHaveSummary) {
        try {
          const summary = await api.getMatchSummary(match.id);
          summaries[match.id] = summary;
        } catch (err) {
          // No summary exists yet, that's okay
        }
      }
    }
    setMatchSummaries(summaries);
  }, [matches]);

  useEffect(() => {
    // Load summaries for all matches
    if (matches.length > 0) {
      loadSummaries();
    }
  }, [matches, loadSummaries]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (!formData.matchDate) {
        setError('Please select a match date and time');
        setLoading(false);
        return;
      }

      const matchData = {
        ...formData,
        // Send as local datetime (no timezone) so the backend stores the time the user picked.
        matchDate: formData.matchDate.length === 16
          ? `${formData.matchDate}:00`
          : formData.matchDate,
      };
      await api.createMatch(clubId, matchData, user.id);
      setSuccess('Match scheduled successfully!');
      setFormData({ title: '', description: '', matchDate: '', location: '', opponentName: '' });
      setShowForm(false);
      loadMatches();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to schedule match');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="match-scheduling">
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <button
          onClick={() => navigate(soccerPath(`/clubs/${clubId}`))}
          className="btn btn-secondary"
          style={{ padding: '0.5rem 1rem', fontSize: '0.9rem' }}
        >
          ← Back to Club
        </button>
        <h2 style={{ flex: '1', minWidth: '200px' }}>Match Scheduling</h2>
      </div>
      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <button
        onClick={() => setShowForm(!showForm)}
        className="btn btn-primary"
        style={{ marginBottom: '1rem' }}
      >
        {showForm ? 'Cancel' : 'Schedule New Match'}
      </button>

      {showForm && (
        <div className="card" style={{ marginBottom: '2rem' }}>
          <h3>Create Match</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Title *</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows="3"
              />
            </div>
            <div className="form-group">
              <label>Match Date & Time *</label>
              <input
                type="datetime-local"
                name="matchDate"
                value={formData.matchDate}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <label>Location</label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Opponent Name</label>
              <input
                type="text"
                name="opponentName"
                value={formData.opponentName}
                onChange={handleChange}
                placeholder="Optional"
              />
            </div>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Scheduling...' : 'Schedule Match'}
            </button>
          </form>
        </div>
      )}

      <div className="matches-list">
        <h3>Upcoming Matches</h3>
        {matches.length === 0 ? (
          <p>No matches scheduled yet.</p>
        ) : (
          matches.map((match) => (
            <div key={match.id} className="match-card">
              <h4>{match.title}</h4>
              {match.description && <p>{match.description}</p>}
              <p><strong>Date:</strong> {new Date(match.matchDate).toLocaleString()}</p>
              {match.location && <p><strong>Location:</strong> {match.location}</p>}
              {match.opponentName && <p><strong>Opponent:</strong> {match.opponentName}</p>}
              <p><strong>Status:</strong> {match.status}</p>
              
              {(() => {
                const matchDate = new Date(match.matchDate);
                const isPast = matchDate < new Date();
                const canShowSummary = match.status === 'COMPLETED' || (match.status === 'SCHEDULED' && isPast);
                return canShowSummary && matchSummaries[match.id] && (
                  <div className="summary-preview" style={{ marginTop: '1rem', padding: '1rem', background: '#f0f0f0', borderRadius: '4px' }}>
                    <h5>Match Summary</h5>
                    {matchSummaries[match.id].scoreHome !== null && matchSummaries[match.id].scoreAway !== null && (
                      <p><strong>Score:</strong> {matchSummaries[match.id].scoreHome} - {matchSummaries[match.id].scoreAway}</p>
                    )}
                    {matchSummaries[match.id].manOfMatchPlayerName && (
                      <p><strong>Man of the Match:</strong> {matchSummaries[match.id].manOfMatchPlayerName}</p>
                    )}
                    {matchSummaries[match.id].photoUrls && matchSummaries[match.id].photoUrls.length > 0 && (
                      <p><strong>Photos:</strong> {matchSummaries[match.id].photoUrls.length} photo(s)</p>
                    )}
                  </div>
                );
              })()}
              
              <div className="match-card__actions">
                <Link to={soccerPath(`/matches/${match.id}`)} className="btn btn-secondary">
                  View Details & Vote Availability
                </Link>
                {(() => {
                  const matchDate = new Date(match.matchDate);
                  const isPast = matchDate < new Date();
                  const canCreateSummary = match.status === 'COMPLETED' || (match.status === 'SCHEDULED' && isPast);
                  return canCreateSummary && (canShareLink() || match.createdByUserId === user?.id) && (
                    <Link to={soccerPath(`/matches/${match.id}/summary`)} className="btn btn-primary">
                      {matchSummaries[match.id] ? 'Update Summary' : 'Create Summary'}
                    </Link>
                  );
                })()}
                {canShareLink() && match.status !== 'COMPLETED' && new Date(match.matchDate) >= new Date() && (
                  <button
                    onClick={() => shareVoteLink(match.id)}
                    className="btn btn-primary"
                    style={{ backgroundColor: '#25D366' }}
                  >
                    📱 Share Vote Link
                  </button>
                )}
                {canShareLink() && match.status === 'SCHEDULED' && (
                  <Link
                    to={soccerPath(`/clubs/${clubId}/matches/${match.id}/preview`)}
                    className="btn btn-primary"
                  >
                    Manage Match Preview
                  </Link>
                )}
                {match.publishedToPublic && match.previewShareToken && (
                  <Link
                    to={soccerPath(`/public/match-preview/${match.previewShareToken}`)}
                    className="btn btn-secondary"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View Published Preview
                  </Link>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default MatchScheduling;

